import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useOptimizedNavigation } from '@/hooks/useOptimizedNavigation';
import { useRequestCancellation } from '@/hooks/useRequestCancellation';
import { realtimeSubscriptionPool } from '@/lib/realtimeSubscriptionPool';
import { getProjectIdFromPath } from '@/utils/route';
import { globalFeatureFlags } from '@/lib/featureFlags';

/**
 * Global component that monitors route transitions and performs cleanup
 * when navigating away from project detail pages to prevent query cascade issues
 */
export const RouteTransitionCleanup = () => {
  const location = useLocation();
  const prevPathRef = useRef<string>(location.pathname);
  const { cleanupProjectQueries } = useOptimizedNavigation();
  const { cancelRequests } = useRequestCancellation();

  useEffect(() => {
    const currentPath = location.pathname;
    const prevPath = prevPathRef.current;

    const prevId = getProjectIdFromPath(prevPath);
    const currId = getProjectIdFromPath(currentPath);

    const wasOnProjectDetail = !!prevId;
    const isNowOnProjectDetail = !!currId;

    const scheduleCleanup = (projectId?: string, capturedPath?: string) => {
      // Stable token: only cleanup if we're still away from this path
      const shouldCleanup = () => capturedPath && location.pathname !== capturedPath;

      // Phase 0: CRITICAL (sync) - abort in-flight requests
      try {
        cancelRequests();
      } catch (err) {
        console.debug('⚠️ Phase 0 (abort) error:', err);
      }

      // Phase 1: HIGH PRIORITY (microtask) - query cleanup
      queueMicrotask(() => {
        if (capturedPath && !shouldCleanup()) return;
        
        try {
          cleanupProjectQueries(projectId);
          console.debug('✅ Phase 1: Query cleanup done', { projectId });
        } catch (err) {
          console.debug('⚠️ Phase 1 (query) error:', err);
        }

        // Phase 2: LOW PRIORITY (next frame) - realtime + DOM cleanup
        requestAnimationFrame(() => {
          if (capturedPath && !shouldCleanup()) return;

          try {
            const unsubCount = realtimeSubscriptionPool.unsubscribeByPattern(
              /^(public:(diary_pins|diary_spec_items|diary_floorplan_layers|diary_specs_layers|items|procurement_.*|payment_milestones))/
            );
            
            // Nuke Execute overlays
            document.querySelectorAll('#execute-portal-root,[data-xc="execute-portal-root"]').forEach(el => el.remove());
            document.querySelectorAll('.execute-root').forEach(el => {
              (el as HTMLElement).style.display = 'none';
              el.setAttribute('data-execute-hidden', 'true');
            });

            console.debug('✅ Phase 2: Realtime + DOM cleanup done', { projectId, unsubCount });
          } catch (err) {
            console.debug('⚠️ Phase 2 (realtime/DOM) error:', err);
          }

        // Phase 3: SAFETY NET (delayed fallback)
        setTimeout(() => {
          const executeRootStillHere = !!document.querySelector('.execute-root,#execute-portal-root');
          const stillOffProject = !/^\/projects\/[^/]+/.test(window.location.pathname);
          
          if (executeRootStillHere && stillOffProject) {
            // ✅ Soft cleanup first - remove lingering DOM artifacts
            try {
              document.querySelectorAll('.execute-root,#execute-portal-root').forEach(el => el.remove());
              console.debug('✅ Soft cleanup: Removed lingering Execute artifacts');
            } catch (err) {
              console.debug('⚠️ Soft cleanup failed:', err);
            }
            
            // ✅ Track event for telemetry
            if (typeof window !== 'undefined' && (window as any).track) {
              (window as any).track('hard_reload_prevented', {
                reason: 'execute-artifacts',
                path: window.location.pathname,
                timestamp: Date.now()
              });
            }
            
            // ✅ Only reload if flag is enabled (default: false)
            if (globalFeatureFlags.isEnabled('ENABLE_SAFE_NAVIGATION_AUTO_RELOAD')) {
              console.warn('⚠️ Execute artifacts remain after 600ms → hard reload (flag enabled)');
              window.location.reload();
            } else {
              console.warn('⚠️ Execute artifacts remain after 600ms → soft cleanup only (auto-reload disabled)');
            }
          }
        }, 600);
        });
      });

      // No cancellation needed - microtask/rAF already scheduled
      return () => {};
    };

    // Case 1: Leaving project detail → cleanup scoped to prevId
    if (wasOnProjectDetail && !isNowOnProjectDetail) {
      performance.mark('nav:cleanup-start');
      console.debug('🧹 RouteTransitionCleanup: Leaving project detail, scheduling cleanup...', {
        from: prevPath,
        to: currentPath,
        projectId: prevId
      });
      const cancel = scheduleCleanup(prevId, prevPath);
      prevPathRef.current = currentPath;
      return cancel;
    }

    // Case 2: Switching between different project details → cleanup prevId
    if (wasOnProjectDetail && isNowOnProjectDetail && prevId !== currId) {
      performance.mark('nav:cleanup-start');
      console.debug('🧹 RouteTransitionCleanup: Switching project detail, scheduling cleanup for previous project...', {
        from: prevId,
        to: currId
      });
      const cancel = scheduleCleanup(prevId, prevPath);
      prevPathRef.current = currentPath;
      return cancel;
    }

    // Case 3: Default - update prevPath
    prevPathRef.current = currentPath;
  }, [location.pathname, cleanupProjectQueries, cancelRequests]);

  return null;
};
